import axios from 'axios';
import queryString from 'query-string';
import {storage} from '../utils';
import {store} from '../store';
import {signout} from '../store/actions/signout';
import {showAlert} from '../store/actions/alert';
import env from '../config/environment';
import {
  ISigninReq,
  IEmployeeChangePassReq,
  IApprovalSettingsItemReq,
  IAdminUsersReq,
  IChangePasswordReq,
  IRenewPasswordReq,
  IEmployeePostReq,
  IEmployeePatchReq,
  IDepartmentPostReq,
  IDepartmentPatchReq,
  IPositionPostReq,
  IPositionPatchReq,
  JobApplicantReportParam,
  AttendanceDetailReportParam,
  AttendanceSummaryReportParam,
  AttendanceDetailPatchReq,
  JobPositionPostReq,
  JobPositionPatchReq,
} from '../config/interface';

const baseUrl = env.api;
const api = {
  signin: (signin: ISigninReq) => {
    return axios.post(`${baseUrl}/auth/admin/login`, signin);
  },
  renewPassword: (renewPassword: IRenewPasswordReq) => {
    return axios.patch(`${baseUrl}/auth/admin/password`, renewPassword);
  },
  getProfile: () => {
    return axios.get(`${baseUrl}/users/me`);
  },
  changePassword: (req: IChangePasswordReq) => {
    return axios.patch(`${baseUrl}/users/me`, req);
  },
  getEmployee: () => {
    return axios.get(`${baseUrl}/users`);
  },
  postEmployee: (req: IEmployeePostReq) => {
    return axios.post(`${baseUrl}/users`, req);
  },
  patchEmployee: (id: number, req: IEmployeePatchReq) => {
    return axios.patch(`${baseUrl}/users/${id}`, req);
  },
  deleteEmployee: (id: number) => {
    return axios.post(`${baseUrl}/users/${id}/deactivate`);
  },
  reactiveUser: (id: number) => {
    return axios.post(`${baseUrl}/users/${id}/activate `);
  },
  changeEmployeePassword: (id: number, req: IEmployeeChangePassReq) => {
    return axios.patch(`${baseUrl}/users/${id}`, req);
  },
  getJobPositions: () => {
    return axios.get(`${baseUrl}/job-positions`);
  },
  createJobPosition: (req: JobPositionPostReq) => {
    return axios.post(`${baseUrl}/job-positions`, req);
  },
  updateJobPosition: (id: number, req: JobPositionPatchReq) => {
    return axios.patch(`${baseUrl}/job-positions/${id}`, req);
  },
  getDepartments: () => {
    return axios.get(`${baseUrl}/departments`);
  },
  postDepartment: (req: IDepartmentPostReq) => {
    return axios.post(`${baseUrl}/departments`, req);
  },
  patchDepartment: (id: number, req: IDepartmentPatchReq) => {
    return axios.patch(`${baseUrl}/departments/${id}`, req);
  },
  getPositions: () => {
    return axios.get(`${baseUrl}/positions`);
  },
  postPosition: (req: IPositionPostReq) => {
    return axios.post(`${baseUrl}/positions`, req);
  },
  patchPosition: (id: number, req: IPositionPatchReq) => {
    return axios.patch(`${baseUrl}/positions/${id}`, req);
  },
  getApprovalSettings: () => {
    return axios.get(`${baseUrl}/approval-settings`);
  },
  postApprovalSettings: (req: IApprovalSettingsItemReq) => {
    return axios.post(`${baseUrl}/approval-settings`, req);
  },
  patchApprovalSettings: (id: number, req: IApprovalSettingsItemReq) => {
    return axios.patch(`${baseUrl}/approval-settings/${id}`, req);
  },
  deleteApprovalSettings: (id: number) => {
    return axios.delete(`${baseUrl}/approval-settings/${id}`);
  },
  getAdminUsers: () => {
    return axios.get(`${baseUrl}/admins`);
  },
  patchPriviledgeAdminUsers: (id: number, req: IAdminUsersReq) => {
    return axios.patch(`${baseUrl}/admins/${id}`, req);
  },
  getAttendanceApprovalReports: (startDate: string, endDate: string) => {
    return axios.get(
      `${baseUrl}/attendance/approval/report?start=${startDate}&end=${endDate}`,
    );
  },
  getAttendanceApprovalDetail: (date: Date) => {
    return axios.get(
      `${baseUrl}/attendance/approval/report/detail?date=${date}`,
    );
  },
  getAttendanceSubmissionReports: (startDate: string, endDate: string) => {
    return axios.get(
      `${baseUrl}/attendance/submission/report?start=${startDate}&end=${endDate}`,
    );
  },
  getAttendanceSubmissionDetail: (query: string) => {
    return axios.get(`${baseUrl}/attendance/submission?${query}`);
  },
  uploadImageAttachment: (req: FormData) => {
    return axios.post(`${baseUrl}/media/image`, req);
  },
  uploadVideoAttachment: (req: FormData) => {
    return axios.post(`${baseUrl}/media/video`, req);
  },
  getJobPost: () => {
    return axios.get(`${baseUrl}/postings`);
  },
  getJobApplicantReport: (
    jobPostingId: string,
    param: JobApplicantReportParam,
  ) => {
    return axios.get(
      `${baseUrl}/applications/postings/${jobPostingId}/exam-report?${queryString.stringify(
        param,
      )}`,
    );
  },
  getJobApplicantReportExport: (
    jobPostingId: string,
    param: JobApplicantReportParam,
  ) => {
    return axios.get(
      `${baseUrl}/applications/postings/${jobPostingId}/exam-report/export?${queryString.stringify(
        param,
      )}`,
      {
        responseType: 'arraybuffer',
      },
    );
  },
  getAttendanceDetailReport: (param: AttendanceDetailReportParam) => {
    return axios.get(
      `${baseUrl}/attendance/details?${queryString.stringify(param)}`,
    );
  },
  getAttendanceDetailReportExport: (param: AttendanceDetailReportParam) => {
    return axios.get(
      `${baseUrl}/attendance/details/export?${queryString.stringify(param)}`,
      {
        responseType: 'arraybuffer',
      },
    );
  },
  patchAttendanceDetail: (id: number, req: AttendanceDetailPatchReq) => {
    return axios.patch(`${baseUrl}/attendance/${id}`, req);
  },
  getAttendanceSummaryReport: (param: AttendanceSummaryReportParam) => {
    return axios.get(
      `${baseUrl}/attendance/summary?${queryString.stringify(param)}`,
    );
  },
  getAttendanceSummaryReportExport: (param: AttendanceSummaryReportParam) => {
    return axios.get(
      `${baseUrl}/attendance/summary/export?${queryString.stringify(param)}`,
      {
        responseType: 'arraybuffer',
      },
    );
  },
};

axios.interceptors.request.use(
  (config) => {
    const auth = storage.getToken();
    if (auth) {
      config.headers.authorization = auth;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add a 401 response interceptor
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (
      error.response &&
      error.response.status === 401 &&
      window.location.pathname !== '/signin'
    ) {
      if (error.response.data && error.response.data.error_code === 4003) {
        store.dispatch<any>(
          showAlert({
            type: 'warning',
            message: '4003',
          }),
        );
      }

      store.dispatch<any>(signout());
    }

    return Promise.reject(error);
  },
);

export {api as default, baseUrl};
